// assets/media-gallery.js
// Replacement for your current MediaGallery code (keeps slider behaviour on mobile, no display:block/none swaps)

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer || !this.elements.thumbnails) return;

        // Listen for slide changes (desktop carousel)
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        // Thumbnail click / touch support
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumb) => {
          const btn = thumb.querySelector('button');
          if (!btn) return;

          const activate = (e) => {
            // click: prevent default + stop propagation
            if (e && e.cancelable) e.preventDefault();
            if (e) e.stopPropagation();

            this.setActiveMedia(thumb.dataset.target, false);
          };

          btn.addEventListener('click', activate);

          // touchstart should NOT be passive if we call preventDefault
          btn.addEventListener('touchstart', activate, { passive: false });
        });

        // If using thumbnail layout on desktop, remove list semantics (theme behaviour)
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        const mediaId = event?.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const viewer = this.elements.viewer;

        const activeMedia =
          viewer.querySelector(`[data-media-id="${mediaId}"]`) || viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        // Remove old active states
        viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        // Reorder if necessary
        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumb?.parentElement?.firstChild !== activeThumb) {
              activeThumb.parentElement.prepend(activeThumb);
            }
          }

          if (viewer.slider) viewer.resetPages();
        }

        // Prevent sticky header reveal
        this.preventStickyHeader();

        // Keep slider layout on ALL viewports:
        // - Desktop: smooth scroll
        // - Mobile: instant scroll (prevents layout/height jumps)
        const behavior = this.mql.matches ? 'smooth' : 'auto';
        if (activeMedia?.parentElement?.scrollTo) {
          activeMedia.parentElement.scrollTo({
            left: activeMedia.offsetLeft,
            behavior,
          });
        }

        // Play video or model
        this.playActiveMedia(activeMedia);

        // Update thumbnail highlight + live region
        if (this.elements.thumbnails) {
          const activeThumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumb);

          if (activeThumb?.dataset?.mediaPosition) {
            this.announceLiveRegion(activeMedia, activeThumb.dataset.mediaPosition);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // Clear previous aria-current
        this.elements.thumbnails.querySelectorAll('button').forEach((btn) =>
          btn.removeAttribute('aria-current')
        );

        // Set current
        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', true);

        // Ensure active thumb is visible if slider supports it
        if (this.elements.thumbnails.isSlideVisible && !this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
          this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft, behavior: 'auto' });
        }
      }

      announceLiveRegion(activeItem, position) {
        // Avoid forcing image reload (image.src = image.src) which can cause reflow/jumps
        if (!this.elements.liveRegion) return;

        const image = activeItem.querySelector('.product__modal-opener--image img');
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        if (!image) {
          announce();
          return;
        }

        if (image.complete) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
